import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../general/components/Toast.hook';
import { useAppStoreSelector } from '../../../store';
import axios from '../../../utils/api/axiosInstance';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';

import { useTheme } from '@mui/material/styles';

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import TranslateIcon from '@mui/icons-material/Translate';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';

import { DataGridPro, GridPaginationModel, GridRenderCellParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';

interface Language {
  LanguageID: number;
  ParentID?: number;
  LangCode: string;
  LangName: string;
  IsDefault: number;
  created_by: string;
  updated_by: string;
  created_on: string;
  modified_on: string;
}

type TranslationStatus = 'missing' | 'unconfirmed' | 'confirmed' | 'locked';

interface TranslationText {
  id: number;
  text: string;
  lastUpdated: string;
  status: TranslationStatus;
}

interface TranslationEntity {
  entityId: string | number;
  table: string;
  field: string;
  translations: Record<string, TranslationText>;
  // table specific
  externalKey?: string | null;
  assetId?: string | number | null;
  productId?: string | number | null;
  attributeId?: string | number | null;
  assetLabel?: string | null;
  attributeLabel?: string | null;
  productLabel?: string | null;
  listId?: string | number | null;
  listLabel?: string | null;
  configurationId?: string | number | null;
}

const getCountryCode = (localeTag: string): string | null => {
  const parts = localeTag.split('-');
  if (parts.length > 1) return parts[parts.length - 1].toLowerCase();
  return null;
};

const renderFlag = (localeTag: string, size: 'small' | 'medium' = 'medium') => {
  const countryCode = getCountryCode(localeTag);
  const sizeStyles = size === 'small'
    ? { width: '20px', height: '14px', fontSize: '1rem' }
    : { width: '24px', height: '16px', fontSize: '1.2rem' };

  if (countryCode) {
    return (
      <Box
        component="span"
        className={`fi fi-${countryCode}`}
        sx={{
          ...sizeStyles,
          borderRadius: '2px',
          overflow: 'hidden',
          display: 'inline-block',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
        onError={(e) => {
          // fallback globe emoji
          e.currentTarget.style.display = 'none';
          const globeElement = document.createElement('span');
          globeElement.textContent = '🌐';
          globeElement.style.fontSize = size === 'small' ? '14px' : '16px';
          e.currentTarget.parentNode?.appendChild(globeElement);
        }}
      />
    );
  } else {
    // fallback globe emoji
    return (
      <Box
        component="span"
        sx={{
          ...sizeStyles,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size === 'small' ? '14px' : '16px'
        }}
      >
        🌐
      </Box>
    );
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

const TABLE_DISPLAY_NAMES: Record<string, string> = {
  'tbladib_dam': 'DAM Assets - Labels',
  'tbladib_pim': 'PIM Products - Labels',
  'tbladib_damattribute': 'DAM Attributes - Labels',
  'tbladib_pimattribute': 'PIM Attributes - Labels',
  'tbladib_foreignkey_list': 'Options Lists - Valebs',
  'tbladib_foreignkey_value': 'Options Lists - Values',
  'tbladib_damattributevalue': 'DAM Attributes - Values',
  'tbladib_pimattributevalue': 'PIM Attributes - Values',
  'tbladib_mdm_configurationvalue': 'Configuration - Values'
};

const ALL_TRANSLATION_TABLES = [
  // JSON
  'tbladib_dam',
  'tbladib_pim',
  'tbladib_damattribute',
  'tbladib_pimattribute',
  'tbladib_foreignkey_list',
  'tbladib_foreignkey_value',
  // LanguageID
  'tbladib_damattributevalue',
  'tbladib_pimattributevalue',
  'tbladib_mdm_configurationvalue'
];

const getTableSpecificColumns = (tableName: string): Array<{ id: string, label: string }> => {
  switch (tableName) {
    case 'tbladib_dam':
      return [
        { id: 'assetId', label: 'Asset ID' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_damattribute':
      return [
        { id: 'attributeId', label: 'DAM Attribute ID' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_damattributevalue':
      return [
        { id: 'assetLabel', label: 'Asset Label' },
        { id: 'attributeLabel', label: 'Attribute Label' }
      ];
    case 'tbladib_pim':
      return [
        { id: 'productId', label: 'Product ID' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_pimattribute':
      return [
        { id: 'attributeId', label: 'PIM Attribute ID' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_pimattributevalue':
      return [
        { id: 'productLabel', label: 'Product Label' },
        { id: 'attributeLabel', label: 'Attribute Label' }
      ];
    case 'tbladib_foreignkey_list':
      return [
        { id: 'listId', label: 'List ID' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_foreignkey_value':
      return [
        { id: 'listLabel', label: 'List Label' },
        { id: 'externalKey', label: 'External Key' }
      ];
    case 'tbladib_mdm_configurationvalue':
      return [
        { id: 'configurationId', label: 'Configuration ID' },
        { id: 'field', label: 'Configuration Label' }
      ];
    default:
      return [];
  }
};

export default function TranslationsTable() {
  const isDefaultLanguage = (langCode: string): boolean => {
    return languages.some(lang => lang.LangCode === langCode && lang.IsDefault === 1);
  };

  const userState = useAppStoreSelector((state) => state.user);
  const { t } = useTranslation();
  const theme = useTheme();

  const [triggerMessage] = useToast();
  const [translationEntities, setTranslationEntities] = useState<TranslationEntity[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [tableNames] = useState<string[]>(ALL_TRANSLATION_TABLES);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<'all' | TranslationStatus>('all');
  const [sortField, setSortField] = useState<string>('entityId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingCell, setEditingCell] = useState<{ entityId: string | number, langCode: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [originalEditValue, setOriginalEditValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);

  const tableSpecificColumns = getTableSpecificColumns(selectedTable || '');

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

  const [columnFilters] = useState<Record<string, string>>({});
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [appliedGlobalSearch, setAppliedGlobalSearch] = useState<string>('');

  const currentRequestRef = useRef<number>(0);

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [totalRows, setTotalRows] = useState(0);

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'LanguageID', sort: 'asc' }]);

  const resetEditingState = () => {
    setEditingCell(null);
    setEditValue('');
    setOriginalEditValue('');
  };

  const handleEditStart = (entityId: string | number, langCode: string, currentText: string) => {
    const entity = translationEntities.find(e => e.entityId === entityId);
    if (entity) {
      const translation = entity.translations[langCode];
      // dont allow edit in locked translations
      if (translation && translation.status === 'locked') {
        triggerMessage("This translation is locked and cannot be edited", true);
        return;
      }
    }

    setEditingCell({ entityId, langCode });
    setEditValue(currentText);
    setOriginalEditValue(currentText);
  };

  const handleSaveTranslation = async (forceStatus?: TranslationStatus, cellData?: { entityId?: string | number, langCode?: string }) => {
    if (editingCell === null && cellData == null) return;

    // check if the value has actually changed
    if (editValue === originalEditValue && !forceStatus) {
      resetEditingState();
      return;
    }

    setIsLoading(true);

    try {
      const { entityId, langCode } = editingCell ?? cellData ?? {};
      const entity = translationEntities.find(e => e.entityId === entityId);

      if (!entity || !langCode) {
        console.error("Entity or language code not found");
        triggerMessage("Entity or language code not found", true);
        return;
      }

      const existingTranslation = entity.translations[langCode];

      // use existing translation text when just changing status (no active editing cell)
      const textToSave = editingCell ? editValue : (existingTranslation?.text || '');

      const newStatus: TranslationStatus = forceStatus || (textToSave.trim() === ''
        ? 'missing'
        : existingTranslation?.status === 'locked'
          ? 'locked'
          : 'confirmed');

      const updatedTranslation = {
        id: existingTranslation?.id || 0, // 0 indicates a new translation
        table: entity.table,
        field: entity.field,
        sourceRowId: entityId,
        languageCode: langCode,
        language: langCode,
        text: textToSave,
        hasMissing: textToSave.trim() === '',
        status: newStatus,
        lastUpdated: new Date().toISOString(),
        // table specific fields
        externalKey: entity.externalKey,
        assetId: entity.assetId,
        productId: entity.productId,
        attributeId: entity.attributeId,
        assetLabel: entity.assetLabel,
        attributeLabel: entity.attributeLabel,
        productLabel: entity.productLabel,
        listId: entity.listId,
        listLabel: entity.listLabel,
        configurationId: entity.configurationId
      };

      const username = userState.data.user?.username || 'unknown';

      const response = await axios.post('/api/mdm/config/saveTranslation', {
        translation: updatedTranslation,
        username: username
      });

      if (response.data && response.data.success) {
        // update UI on success
        setTranslationEntities(prev =>
          prev.map(item => {
            if (item.entityId === entityId && langCode) {
              const updatedTranslations = { ...item.translations };
              updatedTranslations[langCode] = {
                id: response.data.id || existingTranslation?.id || 0,
                text: textToSave,
                lastUpdated: new Date().toISOString(),
                status: newStatus
              };

              return {
                ...item,
                translations: updatedTranslations
              };
            }
            return item;
          })
        );

        triggerMessage("Translation saved successfully", false);
      } else {
        const errorMsg = response.data?.message || 'Unknown error';
        console.error('Failed to save translation:', errorMsg);
        triggerMessage(`Failed to save translation: ${errorMsg}`, true);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Unknown error';
      console.error('Error saving translation:', errorMsg);
      triggerMessage(`Error saving translation: ${errorMsg}`, true);
    } finally {
      resetEditingState();
      setIsLoading(false);
    }
  };

  const handleAutoTranslate = async (entityId: string | number, targetLangCode: string) => {
    setIsLoading(true);

    try {
      const entity = translationEntities.find(e => e.entityId === entityId);

      if (!entity) {
        console.error("Entity not found");
        triggerMessage("Entity not found", true);
        return;
      }

      // find any available translation as source (prefer default language if available)
      const defaultLanguage = languages.find(lang => lang.IsDefault === 1);
      const defaultLangCode = defaultLanguage?.LangCode;

      // first try to use default language as source (must be confirmed)
      let sourceText = defaultLangCode ? entity.translations[defaultLangCode]?.text : null;
      let sourceLangCode = defaultLangCode;
      let sourceConfirmed = defaultLangCode ?
        (entity.translations[defaultLangCode]?.status === 'confirmed') : false;

      // if default language doesnt have a confirmed translation, find any other language with confirmed content
      if (!sourceText || !sourceConfirmed) {
        const availableTranslation = Object.entries(entity.translations).find(
          ([code, trans]) =>
            trans.text &&
            code !== targetLangCode &&
            trans.status === 'confirmed'
        );

        if (availableTranslation) {
          sourceLangCode = availableTranslation[0];
          sourceText = availableTranslation[1].text;
          sourceConfirmed = true;
        } else {
          triggerMessage("No confirmed source translation found in any language", true);
          setIsLoading(false);
          return;
        }
      }

      const response = await axios.post('/api/mdm/config/autoTranslate', {
        sourceText,
        sourceLangCode,
        targetLangCode
      });

      if (response.data && response.data.success) {
        // for auto translate, need to handle the saving differently to mark as unconfirmed
        const translatedText = response.data.translatedText;
        setIsLoading(true);

        try {
          const entity = translationEntities.find(e => e.entityId === entityId);

          if (!entity) {
            console.error("Entity not found");
            triggerMessage("Entity not found", true);
            return;
          }

          const existingTranslation = entity.translations[targetLangCode];

          const newStatus: TranslationStatus = translatedText.trim() === '' ? 'missing' : 'unconfirmed';

          const updatedTranslation = {
            id: existingTranslation?.id || 0,
            table: entity.table,
            field: entity.field,
            sourceRowId: entityId,
            languageCode: targetLangCode,
            language: targetLangCode,
            text: translatedText,
            hasMissing: translatedText.trim() === '',
            status: newStatus,
            lastUpdated: new Date().toISOString(),
            // table specific fields
            externalKey: entity.externalKey,
            assetId: entity.assetId,
            productId: entity.productId,
            attributeId: entity.attributeId,
            assetLabel: entity.assetLabel,
            attributeLabel: entity.attributeLabel,
            productLabel: entity.productLabel,
            listId: entity.listId,
            listLabel: entity.listLabel,
            configurationId: entity.configurationId
          };

          const username = userState.data.user?.username || 'unknown';

          const saveResponse = await axios.post('/api/mdm/config/saveTranslation', {
            translation: updatedTranslation,
            username: username
          });

          if (saveResponse.data && saveResponse.data.success) {
            // update UI on success
            setTranslationEntities(prev =>
              prev.map(item => {
                if (item.entityId === entityId) {
                  const updatedTranslations = { ...item.translations };
                  updatedTranslations[targetLangCode] = {
                    id: saveResponse.data.id || existingTranslation?.id || 0,
                    text: translatedText,
                    lastUpdated: new Date().toISOString(),
                    status: newStatus
                  };

                  return {
                    ...item,
                    translations: updatedTranslations
                  };
                }
                return item;
              })
            );

            triggerMessage(`Auto-translation applied from ${sourceLangCode} (marked as unconfirmed)`, false);
          } else {
            const errorMsg = saveResponse.data?.message || 'Unknown error';
            console.error('Failed to save auto-translation:', errorMsg);
            triggerMessage(`Failed to save auto-translation: ${errorMsg}`, true);
          }
        } catch (error: any) {
          const errorMsg = error.message || 'Unknown error';
          console.error('Error saving auto-translation:', errorMsg);
          triggerMessage(`Error saving auto-translation: ${errorMsg}`, true);
        }
      } else {
        const errorMsg = response.data?.message || 'Unknown error';
        console.error('Failed to auto-translate:', errorMsg);
        triggerMessage(`Failed to auto-translate: ${errorMsg}`, true);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Unknown error';
      console.error('Error during auto-translation:', errorMsg);
      triggerMessage(`Error during auto-translation: ${errorMsg}`, true);
    } finally {
      setIsLoading(false);
      resetEditingState();
    }
  };

  // since we use server side filter, just return as is
  const getFilteredAndSortedData = () => {
    return translationEntities;
  };

  const handleApplyGlobalSearch = async () => {
    const searchTerm = globalSearch.trim();
    setAppliedGlobalSearch(searchTerm);
    await fetchTranslations(0, sortField, sortDirection, searchTerm);
  };

  const handleClearGlobalSearch = async () => {
    setGlobalSearch('');
    setAppliedGlobalSearch('');
    await fetchTranslations(0, sortField, sortDirection, '');
  };

  const fetchTranslations = async (
    customPage?: number,
    customSortField?: string,
    customSortDirection?: 'asc' | 'desc',
    customGlobalSearch?: string
  ) => {
    if (!selectedTable) {
      setTranslationEntities([]);
      setIsLoading(false);
      return;
    }

    // generate a unique request ID to prevent race conditions
    const requestId = ++currentRequestRef.current;
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      const currentPage = customPage ?? paginationModel.page;
      const pageSize = paginationModel.pageSize;
      const offset = currentPage * pageSize;

      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());

      params.append('offset', offset.toString());
      params.append('limit', pageSize.toString());
      params.append('sortField', customSortField || sortField);
      params.append('sortDirection', customSortDirection || sortDirection);
      params.append('table', selectedTable);

      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }

      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && key !== 'table') { // skip 'table' as it's handled separately
          params.append(key, value);
        }
      });

      const searchTerm = customGlobalSearch !== undefined ? customGlobalSearch : appliedGlobalSearch;
      if (searchTerm && searchTerm.trim()) {
        params.append('globalSearch', searchTerm.trim());
      }

      if (visibleLanguages.length > 0) {
        params.append('visibleLanguages', visibleLanguages.join(','));
      }

      const response = await axios.get(`/api/mdm/config/getTranslations?${params.toString()}`);

      if (requestId !== currentRequestRef.current) {
        return;
      }

      if (response.data && response.data.success) {
        const entities = response.data.data;
        setTranslationEntities(entities);

        if (response.data.languages) {
          setLanguages(response.data.languages.map((lang: any) => ({
            LanguageID: lang.id,
            LangCode: lang.code,
            LangName: lang.name,
            IsDefault: lang.isDefault ? 1 : 0,
            created_by: '',
            updated_by: '',
            created_on: '',
            modified_on: ''
          })));

          if (visibleLanguages.length === 0) {
            const allLanguageCodes = response.data.languages.map((lang: any) => lang.code);
            const defaultLang = response.data.languages.find((lang: any) => lang.isDefault);
            const nextLang = response.data.languages.find((lang: any) => !lang.isDefault);

            const defaultLanguages: string[] = [];
            if (defaultLang) {
              defaultLanguages.push(defaultLang.code);
            }
            if (nextLang) {
              defaultLanguages.push(nextLang.code);
            }

            setVisibleLanguages(defaultLanguages.length > 0 ? defaultLanguages : allLanguageCodes);
          }
        }

        if (response.data.meta) {
          const totalItems = response.data.meta.total;
          setTotalRows(totalItems);
        }
      } else {
        console.error('Failed to fetch translations');
      }
    } catch (error) {
      console.error('Error fetching translations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialLanguages = async () => {
      try {
        setIsLoadingLanguages(true);
        const response = await axios.get('/api/mdm/config/getLanguages');
        if (response.data && response.data.success) {
          const fetchedLanguages = response.data.data;
          setLanguages(fetchedLanguages);

          const defaultLang = fetchedLanguages.find((lang: Language) => lang.IsDefault === 1);
          const nextLang = fetchedLanguages.find((lang: Language) => lang.IsDefault !== 1);

          const defaultLanguages: string[] = [];
          if (defaultLang) {
            defaultLanguages.push(defaultLang.LangCode);
          }
          if (nextLang) {
            defaultLanguages.push(nextLang.LangCode);
          }

          setVisibleLanguages(defaultLanguages.length > 0 ? defaultLanguages : fetchedLanguages.map((lang: Language) => lang.LangCode));
        }
      } catch (error) {
        console.error('Error fetching initial languages:', error);
      } finally {
        setIsLoadingLanguages(false);
      }
    };

    fetchInitialLanguages();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      // clear any existing data and state
      setTranslationEntities([]);

      // reset editing state
      resetEditingState();

      // reset sorting to default
      setSortField('entityId');
      setSortDirection('asc');

      // reset global search
      setGlobalSearch('');
      setAppliedGlobalSearch('');

      const newTableColumns = getTableSpecificColumns(selectedTable);

      setVisibleColumns(
        newTableColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
      );

      // reset to first page and fetch data when table changes
      fetchTranslations(0, 'entityId', 'asc');
    } else {
      setTranslationEntities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable]);

  // refresh when status filters change
  useEffect(() => {
    if (selectedTable) {
      fetchTranslations(
        paginationModel.page,
        sortField,
        sortDirection,
        appliedGlobalSearch
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  const filteredAndSortedData = getFilteredAndSortedData();

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {isLoading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10
        }}>
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ mb: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 1, mt: '-8px', ml: '40px' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('mdm.translationsManagement')}
          </Typography>
        </Box>

        {/* All controls in one row */}
        <Box sx={{ mt: 7, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <Box sx={{ width: 300, flexShrink: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="table-select-label">{t('mdm.selectTable')}</InputLabel>
              <Select
                labelId="table-select-label"
                id="table-select"
                value={selectedTable || ''}
                label="Select Table"
                size="small"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    setSelectedTable(value);
                    setGlobalSearch('');
                    setAppliedGlobalSearch('');
                    setSelectedStatus('all');
                  }
                }}
              >
                {tableNames.map((table) => (
                  <MenuItem key={table} value={table}>{TABLE_DISPLAY_NAMES[table] || table}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Filters appear when a table is selected */}
          {selectedTable && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* Language Visibility Toggle */}
                <Box sx={{ minWidth: 280 }}>
                  <Autocomplete
                    multiple
                    limitTags={2}
                    disableCloseOnSelect
                    id="language-visibility"
                    size="small"
                    options={languages.map(lang => lang.LangCode)}
                    getOptionLabel={(langCode) => {
                      const lang = languages.find(l => l.LangCode === langCode);
                      return `${langCode} ${lang ? `(${lang.LangName})` : ''}`;
                    }}
                    value={visibleLanguages}
                    onChange={(event, newValue) => {
                      setVisibleLanguages(newValue);
                    }}
                    renderOption={(props, langCode) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box
                          component="li"
                          key={key}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          {...otherProps}
                        >
                          {renderFlag(langCode, 'small')}
                          <span>{langCode}</span>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Visible Languages"
                        placeholder={visibleLanguages.length > 0 ? "" : "All Languages"}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((langCode, index) => {
                        // Show first 2 tags directly, rest are shown as "+X more"
                        if (index < 2) {
                          return (
                            <Chip
                              {...getTagProps({ index })}
                              key={langCode}
                              icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>{renderFlag(langCode, 'small')}</Box>}
                              label={langCode}
                              size="small"
                            />
                          );
                        }
                        return null;
                      })
                    }
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
                {/* Status Filter */}
                <Box sx={{ minWidth: 150 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      value={selectedStatus}
                      label="Status"
                      onChange={(e) => {
                        setSelectedStatus(e.target.value as 'all' | TranslationStatus);
                      }}
                    >
                      <MenuItem value="all">{t('mdm.allStatus')}</MenuItem>
                      <MenuItem value="missing">{t('mdm.missing')}</MenuItem>
                      <MenuItem value="unconfirmed">{t('mdm.unconfirmed')}</MenuItem>
                      <MenuItem value="confirmed">{t('mdm.confirmed')}</MenuItem>
                      <MenuItem value="locked">{t('mdm.locked')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Global Search */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Search"
                    placeholder="Search..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyGlobalSearch();
                      }
                    }}
                    sx={{ width: 180 }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleApplyGlobalSearch}
                    disabled={isLoading}
                  >
                    {t('mdm.apply')}
                  </Button>

                  {appliedGlobalSearch && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearGlobalSearch}
                      disabled={isLoading}
                      sx={{ height: '40px' }}
                    >
                      {t('mdm.clear')}
                    </Button>
                  )}
                </Box>
              </Box>
              {isLoadingLanguages && <CircularProgress size={20} />}
            </>
          )}
        </Box>
      </Box>

      {/* Main table */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {!selectedTable ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <Typography variant="h6" color={theme.palette.text.secondary} gutterBottom>
              {t('mdm.selectTableTranslations')}
            </Typography>
            <Typography variant="body1" color={theme.palette.text.secondary}>
              {t('mdm.useDropdownMenu')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'absolute', left: 0, right: 0 }}>
            <DataGridPro
              sx={{
                mb: 1,
                minWidth: '400px',
                minHeight: '232px',
                maxHeight: 'calc(100vh - 380px)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }
              }}
              pagination
              paginationMode="server"
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => {
                setPaginationModel(prev => ({
                  ...newModel,
                  page: newModel.pageSize !== prev.pageSize ? 0 : newModel.page
                }));
              }}
              checkboxSelection
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setSelectedRows(newRowSelectionModel);
              }}
              disableRowSelectionOnClick
              rowCount={totalRows}
              rowSelectionModel={selectedRows}
              rows={filteredAndSortedData.map((entity) => ({
                id: `${entity.table}_${entity.entityId}`,
                ...entity,
              }))}
              columns={[
                ...tableSpecificColumns
                  .filter((column) => visibleColumns[column.id])
                  .map((column) => ({
                    field: column.id,
                    headerName: column.label,
                    flex: 1,
                    minWidth: 150,
                    sortable: true,
                    renderCell: (params: GridRenderCellParams<any, any>) => {
                      const entity = params.row as TranslationEntity;
                      const rawValue =
                        entity[column.id as keyof TranslationEntity] ??
                        (column.id === 'externalKey' && entity.entityId
                          ? `${entity.entityId}`
                          : column.id === 'assetId' && entity.entityId
                            ? `${entity.entityId}`
                            : column.id === 'productId' && entity.entityId
                              ? `${entity.entityId}`
                              : column.id === 'attributeId' && entity.entityId
                                ? `${entity.entityId}`
                                : column.id === 'configurationId' && entity.entityId
                                  ? `${entity.entityId}`
                                  : column.id === 'listId' && entity.entityId
                                    ? `${entity.entityId}`
                                    : 'N/A');

                      const value =
                        typeof rawValue === 'string' || typeof rawValue === 'number'
                          ? rawValue
                          : 'N/A';

                      return (
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {value}
                        </Typography>
                      );
                    },
                  })),

                ...visibleLanguages.map((langCode) => ({
                  field: `lang_${langCode}`,
                  headerName: langCode,
                  flex: 1.2,
                  minWidth: 220,
                  sortable: false,
                  renderHeader: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {renderFlag(langCode, 'small')}
                      <Typography variant="body2">{langCode}</Typography>
                      {isDefaultLanguage(langCode) && (
                        <Tooltip title="Default language" arrow>
                          <StarIcon sx={{ color: 'gold', fontSize: '1rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  ),
                  renderCell: (params: GridRenderCellParams<any, any>) => {
                    const entity = params.row as TranslationEntity;
                    const translation = entity.translations?.[langCode] as TranslationText | undefined;
                    const isEditing =
                      editingCell &&
                      editingCell.entityId === entity.entityId &&
                      editingCell.langCode === langCode;

                    if (!translation) {
                      const hasConfirmedTranslation = Object.entries(entity.translations ?? {}).some(
                        ([code, trans]) =>
                          code !== langCode &&
                          (trans as TranslationText).text &&
                          (trans as TranslationText).status === 'confirmed'
                      );

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            opacity: 0.5,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleEditStart(entity.entityId, langCode, '')}
                        >
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            {t('mdm.addTranslation')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip
                              title={
                                hasConfirmedTranslation
                                  ? 'Auto-translate'
                                  : 'Auto-translate not available'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!hasConfirmedTranslation}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    hasConfirmedTranslation &&
                                      handleAutoTranslate(entity.entityId, langCode);
                                  }}
                                >
                                  <TranslateIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <IconButton size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    }

                    if (isEditing) {
                      return (
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                          <TextField
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            fullWidth
                            size="small"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveTranslation();
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                                setEditValue('');
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleSaveTranslation()}
                            color="primary"
                            sx={{ 
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              padding: 0.5
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title="Save and Lock" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleSaveTranslation('locked')}
                              color="warning"
                              sx={{ 
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                padding: 0.5
                              }}
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          cursor: translation.status === 'locked' ? 'not-allowed' : 'pointer',
                          py: 0.5,
                          pl: 1,
                          pr: 1,
                          gap: 0.3,
                        }}
                        onClick={() =>
                          handleEditStart(entity.entityId, langCode, translation.text)
                        }
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              fontStyle: translation.text ? 'normal' : 'italic',
                              color:
                                translation.status === 'locked'
                                  ? 'text.disabled'
                                  : translation.text
                                    ? 'text.primary'
                                    : 'text.secondary',
                              wordBreak: 'break-word',
                            }}
                          >
                            {translation.text || 'No translation'}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              width: '100%',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.7rem',
                                textAlign: 'left',
                              }}
                            >
                              {formatDate(translation.lastUpdated)}
                            </Typography>

                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (translation.status === 'unconfirmed') {
                                  handleSaveTranslation('confirmed', {
                                    entityId: entity.entityId,
                                    langCode,
                                  });
                                }
                              }}
                              sx={{
                                backgroundColor:
                                  translation.status === 'missing'
                                    ? theme.palette.error.main
                                    : translation.status === 'confirmed'
                                      ? theme.palette.success.main
                                      : translation.status === 'unconfirmed'
                                        ? theme.palette.warning.main
                                        : 'grey.500',
                                color: 'white',
                                borderRadius: 1,
                                px: 0.7,
                                py: 0.2,
                                fontSize: '0.6rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.2,
                                width: 'fit-content',
                                opacity: 0.8,
                                cursor: translation.status === 'unconfirmed' ? 'pointer' : 'default',
                                '&:hover':
                                  translation.status === 'unconfirmed'
                                    ? {
                                      opacity: 0.9,
                                      transform: 'scale(1.05)',
                                      transition: 'all 0.2s ease',
                                    }
                                    : undefined,
                              }}
                            >
                              {translation.status.charAt(0).toUpperCase() + translation.status.slice(1)}
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            width: '100%',
                            gap: 1,
                          }}
                        >
                          {translation.status !== 'locked' && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditStart(entity.entityId, langCode, translation.text);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Auto-translate" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.5 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAutoTranslate(entity.entityId, langCode);
                                  }}
                                >
                                  <TranslateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>


                      </Box>
                    );
                  },
                })),
              ]}
              getRowHeight={() => 'auto'}
              sortingOrder={["asc", "desc"]}
              sortModel={sortModel}
              onSortModelChange={(m) => setSortModel(m)}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color={theme.palette.text.secondary}>
                      {t('mdm.noTranslationsFound')}
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        )}
      </Box>
    </Box >
  );
}